import React, { useEffect, useRef, useState } from 'react';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  title: React.ReactNode;
  placement?: Placement;
  children: React.ReactNode;
  className?: string;
}

export default function Tooltip({ title, placement = 'top', children, className }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const hideTimeout = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (hideTimeout.current) window.clearTimeout(hideTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (!visible || !wrapperRef.current || !tooltipRef.current) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const GAP = 8;

    let style: React.CSSProperties = { position: 'absolute' };

    if (placement === 'left' || placement === 'right') {
      // Vertical center
      let top = wrapperRect.height / 2 - tooltipRect.height / 2;
      // Clamp vertically
      const absTop = wrapperRect.top + top;
      if (absTop < 0) top -= absTop;
      else if (absTop + tooltipRect.height > viewportHeight)
        top -= absTop + tooltipRect.height - viewportHeight;

      if (placement === 'left') {
        // Check if there's enough space on the left side of the viewport
        const tooltipLeft = wrapperRect.left - GAP - tooltipRect.width;
        if (tooltipLeft < 0) {
          // Not enough space on left — flip to right
          style = { ...style, left: `calc(100% + ${GAP}px)`, top, transform: 'none' };
        } else {
          style = { ...style, right: `calc(100% + ${GAP}px)`, top, transform: 'none' };
        }
      } else {
        // right placement
        const tooltipRight = wrapperRect.right + GAP + tooltipRect.width;
        if (tooltipRight > viewportWidth) {
          // Not enough space on right — flip to left
          style = { ...style, right: `calc(100% + ${GAP}px)`, top, transform: 'none' };
        } else {
          style = { ...style, left: `calc(100% + ${GAP}px)`, top, transform: 'none' };
        }
      }
    } else {
      // top / bottom: center horizontally, clamp to viewport
      const centerLeft = wrapperRect.width / 2 - tooltipRect.width / 2;
      let left = centerLeft;
      const absLeft = wrapperRect.left + left;
      if (absLeft < 0) left -= absLeft;
      else if (absLeft + tooltipRect.width > viewportWidth)
        left -= absLeft + tooltipRect.width - viewportWidth;

      if (placement === 'top') {
        style = { ...style, bottom: `calc(100% + ${GAP}px)`, left, transform: 'none' };
      } else {
        style = { ...style, top: `calc(100% + ${GAP}px)`, left, transform: 'none' };
      }
    }

    setTooltipStyle(style);
  }, [visible, placement]);

  const show = () => {
    if (hideTimeout.current) window.clearTimeout(hideTimeout.current);
    setVisible(true);
  };
  const hide = () => {
    hideTimeout.current = window.setTimeout(() => setVisible(false), 100);
  };

  return (
    <span
      ref={wrapperRef}
      className={`inline-block relative ${className || ''}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      tabIndex={0}
      aria-haspopup="true"
    >
      {children}
      {visible && (
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{
            whiteSpace: 'nowrap',
            position: 'absolute',
            ...tooltipStyle
          }}
          className="bg-gradient-to-br font-gilroy-medium py-[6px] px-[10px] z-[9999] from-gray-100 to-gray-300/50 shadow-md backdrop-blur-sm text-sm border border-gray-200 rounded-lg"
        >
          <div>{title}</div>
        </div>
      )}
    </span>
  );
}
